import type { FilterOption } from './select-filter'
import type { FilterFieldDef, FilterRenderCtx, FilterSurface } from './types'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'

interface RemoteSelectFilterOptions {
    label: () => string
    /** Nhãn cho lựa chọn "tất cả" — giá trị mặc định, không ghi vào URL. */
    allLabel: () => string
    /**
     * Hook trả về danh sách lựa chọn. Được gọi BÊN TRONG control (một
     * component thật) nên dùng được `useQuery` và mọi hook khác.
     */
    useOptions: () => FilterOption<string>[]
}

const controlClass = (surface: FilterSurface) => {
    if (surface === 'sheet') return 'h-11 w-full'
    if (surface === 'outside') return 'h-11 w-full md:h-9'
    return 'h-9 w-full'
}

function RemoteSelectControl({
    id,
    value,
    onChange,
    surface,
    allLabel,
    useOptions,
}: FilterRenderCtx<string> &
    Pick<RemoteSelectFilterOptions, 'allLabel' | 'useOptions'>) {
    const options = useOptions()
    return (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger id={id} className={controlClass(surface)}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="all">{allLabel()}</SelectItem>
                {options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

/**
 * Bộ lọc chọn một giá trị mà danh sách lựa chọn đến TỪ SERVER (loại chè, kho,
 * đơn vị vận chuyển…). Khác `selectFilter` ở chỗ tập giá trị hợp lệ không biết
 * trước lúc khai config, nên:
 *
 * - `parse` chỉ chặn kiểu sai và chuỗi rỗng, không đối chiếu danh sách.
 * - `chipLabel` trả `undefined` — từ id không suy ra được tên. Màn tra tên rồi
 *   truyền qua `chipLabels` của `useListFilters`.
 *
 * Viết thêm được factory này mà không đụng `core` chính là bằng chứng hợp đồng
 * `FilterFieldDef` đủ dùng.
 */
export function remoteSelectFilter({
    label,
    allLabel,
    useOptions,
}: RemoteSelectFilterOptions): FilterFieldDef<string> {
    return {
        defaultValue: 'all',
        label,
        parse: (raw) => (typeof raw === 'string' && raw ? raw : undefined),
        chipLabel: () => undefined,
        render: (ctx) => (
            <RemoteSelectControl
                {...ctx}
                allLabel={allLabel}
                useOptions={useOptions}
            />
        ),
    }
}
